import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../../../atom/Dropdown/Dropdown";
import clsx from "clsx";
import { BlockVisibility, BlockVisibilityType } from "../../../../types/block/enum";
import { CheckIcon } from "../../../../icons";
import VisibilityIcon from "./VisibilityIcon";
import { useState } from "react";
import InfoIcon from "../../../atom/icons/InfoIcon";
import VisibilityTooltip from "./VisibilityTooltip";
import Tooltip from "../../../atom/Tooltip";

const VISIBILITY_LABEL = {
  [BlockVisibility.PUBLIC]: "Public",
  [BlockVisibility.DEEPWORK]: "Deep work",
  [BlockVisibility.INVISIBLE]: "Invisible",
};

const VISIBILITY_TOOLTIP = {
  [BlockVisibility.PUBLIC]: "Anyone can see the details of this event.",
  [BlockVisibility.DEEPWORK]:
    "You can do deep work without interruption.\nIt is marked as busy to other people.",
  [BlockVisibility.INVISIBLE]:
    "You can focus while still collaborating\nwith others.It is invisible to other people.",
};

const VisibilityDropdown = ({
  visibilityDropdownRef,
  selectedVisibility,
  onValueChange,
}: {
  visibilityDropdownRef: React.RefObject<HTMLDivElement>;
  selectedVisibility: BlockVisibilityType;
  onValueChange: (value: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    // esc 눌렀을 때 전파 막기(해당 dropdown만 닫히도록)
    e.stopPropagation();
  };

  const handleDropdownItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      // enter 눌렀을 때 상위 모달 닫히지 않도록 막기
      e.stopPropagation();
    }
  };

  return (
    <DropdownMenu
      modal={false}
      open={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
    >
      <Tooltip
        content="Visibility"
        isShow={isOpen ? false : undefined}
      >
        <DropdownMenuTrigger asChild>
          <button
            className={clsx(
              "flex cursor-pointer select-none items-center justify-center rounded-[5px] bg-transparent p-[4px] text-80",
              "hover:bg-w-8-hover hover:text-white",
              { "bg-w-8-hover": isOpen }
            )}
          >
            <VisibilityIcon
              type={selectedVisibility}
              color={isOpen ? "#fff" : ""}
              className={"fill-80"}
            />
          </button>
        </DropdownMenuTrigger>
      </Tooltip>

      <DropdownMenuContent
        ref={visibilityDropdownRef}
        width="140px"
        sideOffset={5}
        align="end"
        className="w-[170px] text-white"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <DropdownMenuRadioGroup
          value={selectedVisibility}
          onValueChange={onValueChange}
        >
          {Object.values(BlockVisibility).map((visibilityType) => {
            return (
              <DropdownMenuRadioItem
                key={visibilityType}
                value={visibilityType}
                onKeyDown={handleDropdownItemKeyDown}
                className={clsx(
                  "group box-border flex h-8 w-full cursor-pointer items-center justify-between gap-[6px] px-2 text-[12px] text-80 data-[highlighted]:bg-w-8-hover data-[highlighted]:outline-none",
                  selectedVisibility === visibilityType
                    ? "text-moba data-[highlighted]:text-moba"
                    : "text-80 hover:text-white data-[highlighted]:text-white"
                )}
              >
                <VisibilityIcon
                  type={visibilityType}
                  color={selectedVisibility === visibilityType ? "#69E4FF" : ""}
                  className={clsx(
                    "fill-80 text-[16px]",
                    selectedVisibility === visibilityType && "fill-moba"
                  )}
                />
                <p className="flex-1">{VISIBILITY_LABEL[visibilityType as BlockVisibilityType]}</p>
                <div className="flex items-center opacity-0 group-data-[highlighted]:opacity-100">
                  <VisibilityTooltip
                    alignOffset={selectedVisibility === visibilityType ? -29 : -9}
                    content={VISIBILITY_TOOLTIP[visibilityType as BlockVisibilityType]}
                  >
                    <InfoIcon color={"#787D80"} />
                  </VisibilityTooltip>
                </div>
                {selectedVisibility === visibilityType && (
                  <CheckIcon
                    color={"#69E4FF"}
                    className="text-[14px]"
                  />
                )}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default VisibilityDropdown;
